import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import PoweredBy from '../components/PoweredBy';
import Toolbar from './components/Toolbar';
import Canvas from './components/Canvas';
import NodeEditor from './components/NodeEditor';
import LaneManager from './components/LaneManager';
import AxisEditor from './components/AxisEditor';
import ToolHistorySidebar from '../components/ToolHistorySidebar';
import { useLocalStorage } from './hooks/useLocalStorage';
import { toolsData } from '../config';
import {
  getLocalizedToolField,
  getToolStructuredData,
  useSeoMetadata,
} from '../../../utils/seo';
import styles from './index.module.scss';

const INITIAL_LANES = {
  planning: { name: 'Planning', color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', gridBg: '#faf8ff' },
  execution: { name: 'Execution', color: '#2563eb', bg: '#eff6ff', border: '#93c5fd', gridBg: '#f5f9ff' },
  review: { name: 'Review & Improvement', color: '#06b6d4', bg: '#ecfeff', border: '#67e8f9', gridBg: '#f0feff' },
};

const INITIAL_LANE_ORDER = ['planning', 'execution', 'review'];

const INITIAL_AXIS = {
  name: 'Personal Project Roadmap',
  gridDensity: 'auto',
  nodeStyle: 'tag',
  startDate: '2026-03-01',
  endDate: '2026-06-01',
  months: [
    { id: 'm1', date: '2026-03-01', label: 'Mar' },
    { id: 'm2', date: '2026-04-01', label: 'Apr' },
    { id: 'm3', date: '2026-05-01', label: 'May' },
  ],
};

const INITIAL_NODES = [
  { id: 1, label: 'Define goals and success criteria', date: '2026-03-03', cat: 'planning', priority: 'P0', status: 'done' },
  { id: 2, label: 'Create project timeline draft', date: '2026-03-08', cat: 'planning', priority: 'P1', status: 'done' },
  { id: 3, label: 'Estimate budget and resources', date: '2026-03-14', cat: 'planning', priority: 'P1', status: 'doing' },
  { id: 4, label: 'Confirm scope and priorities', date: '2026-03-22', cat: 'planning', priority: 'P0', status: 'todo' },
  { id: 5, label: 'Set weekly milestones', date: '2026-03-28', cat: 'planning', priority: 'P2', status: 'todo' },
  { id: 6, label: 'Build first workable version', date: '2026-04-05', cat: 'execution', priority: 'P0', status: 'doing' },
  { id: 7, label: 'Prepare launch checklist', date: '2026-04-12', cat: 'execution', priority: 'P1', status: 'todo' },
  { id: 8, label: 'Run internal testing', date: '2026-04-18', cat: 'execution', priority: 'P1', status: 'todo' },
  { id: 9, label: 'Fix critical issues', date: '2026-04-24', cat: 'execution', priority: 'P0', status: 'todo' },
  { id: 10, label: 'Release v1 to users', date: '2026-05-02', cat: 'execution', priority: 'P0', status: 'todo' },
  { id: 11, label: 'Collect user feedback', date: '2026-05-06', cat: 'review', priority: 'P1', status: 'todo' },
  { id: 12, label: 'Analyze usage data', date: '2026-05-12', cat: 'review', priority: 'P1', status: 'todo' },
  { id: 13, label: 'Prioritize improvement list', date: '2026-05-18', cat: 'review', priority: 'P1', status: 'todo' },
  { id: 14, label: 'Plan v1.1 updates', date: '2026-05-22', cat: 'review', priority: 'P2', status: 'todo' },
  { id: 15, label: 'Document lessons learned', date: '2026-05-27', cat: 'review', priority: 'P2', status: 'todo' },
  { id: 16, label: 'Share project summary', date: '2026-03-18', cat: 'planning', priority: 'P2', status: 'todo' },
  { id: 17, label: 'Coordinate with stakeholders', date: '2026-04-08', cat: 'execution', priority: 'P1', status: 'doing' },
  { id: 18, label: 'Prepare next-phase proposal', date: '2026-05-30', cat: 'review', priority: 'P2', status: 'todo' },
];

const PROFILE_STORAGE_KEY = 'timeline_tool_profiles';
const PROFILE_ACTIVE_KEY = 'timeline_active_profile_id';
const SIDEBAR_WIDTH_KEY = 'timeline_sidebar_width';
const SIDEBAR_COLLAPSED_KEY = 'timeline_sidebar_collapsed';
const SIDEBAR_MIN_W = 220;
const SIDEBAR_MAX_W = 520;

const createProfileId = () => `timeline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const makeProfile = (name, snapshot) => ({
  id: createProfileId(),
  name,
  updatedAt: Date.now(),
  snapshot,
});

const Timeline = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const timelineTool = toolsData.find((tool) => tool.id === 'timeline');
  const timelineName = getLocalizedToolField(timelineTool, 'name', currentLang);
  const timelineDescription = getLocalizedToolField(timelineTool?.seo, 'geoSummary', currentLang)
    || getLocalizedToolField(timelineTool, 'description', currentLang);

  useSeoMetadata({
    title: `${timelineName} | P2T Tools`,
    description: timelineDescription,
    path: '/tools/timeline',
    lang: currentLang,
    keywords: [
      ...(getLocalizedToolField(timelineTool, 'tags', currentLang) || []),
      ...(getLocalizedToolField(timelineTool?.seo, 'keywords', currentLang) || []),
    ],
    structuredData: getToolStructuredData(timelineTool, currentLang),
  });

  const [nodes, setNodes] = useLocalStorage('timeline_nodes', INITIAL_NODES);
  const [lanes, setLanes] = useLocalStorage('timeline_lanes', INITIAL_LANES);
  const [laneOrder, setLaneOrder] = useLocalStorage('timeline_lane_order', INITIAL_LANE_ORDER);
  const [nextId, setNextId] = useLocalStorage('timeline_next_id', 19);
  const [axisConfig, setAxisConfig] = useLocalStorage('timeline_axis_config', INITIAL_AXIS);
  const [profiles, setProfiles] = useLocalStorage(PROFILE_STORAGE_KEY, []);
  const [activeProfileId, setActiveProfileId] = useLocalStorage(PROFILE_ACTIVE_KEY, '');
  const [sidebarWidth, setSidebarWidth] = useLocalStorage(SIDEBAR_WIDTH_KEY, 300);
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(SIDEBAR_COLLAPSED_KEY, false);

  const [editingNode, setEditingNode] = useState(null);
  const [showLaneManager, setShowLaneManager] = useState(false);
  const [showAxisEditor, setShowAxisEditor] = useState(false);
  const [showAddBar, setShowAddBar] = useState(false);

  const canvasRef = useRef(null);
  const suppressProfileSyncRef = useRef(false);
  const mainContentRef = useRef(null);
  const resizingRef = useRef(false);

  const normalizeNode = useCallback((node) => ({
    ...node,
    status: node.status || 'todo',
  }), []);

  const currentSnapshot = useCallback(() => ({
    nodes: nodes.map(normalizeNode),
    lanes,
    laneOrder,
    nextId,
    axisConfig,
  }), [nodes, normalizeNode, lanes, laneOrder, nextId, axisConfig]);

  const applySnapshot = useCallback((snapshot) => {
    setNodes((snapshot.nodes || INITIAL_NODES).map(normalizeNode));
    setLanes(snapshot.lanes || INITIAL_LANES);
    setLaneOrder(snapshot.laneOrder || INITIAL_LANE_ORDER);
    setNextId(typeof snapshot.nextId === 'number' ? snapshot.nextId : 19);
    setAxisConfig({
      ...INITIAL_AXIS,
      ...(snapshot.axisConfig || {}),
      months: Array.isArray(snapshot.axisConfig?.months)
        ? snapshot.axisConfig.months
        : INITIAL_AXIS.months,
    });
  }, [normalizeNode, setNodes, setLanes, setLaneOrder, setNextId, setAxisConfig]);

  const getDefaultProfileName = useCallback((index) => `${t('tools.common.config')} ${index}`, [t]);

  const ensureProfiles = useCallback(() => {
    if (profiles.length > 0) {
      return;
    }
    const profile = makeProfile(getDefaultProfileName(1), currentSnapshot());
    setProfiles([profile]);
    setActiveProfileId(profile.id);
  }, [profiles.length, getDefaultProfileName]);

  React.useEffect(() => {
    ensureProfiles();
  }, [ensureProfiles]);

  React.useEffect(() => {
    const handleMouseMove = (event) => {
      if (!resizingRef.current || !mainContentRef.current) {
        return;
      }
      const containerRect = mainContentRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - containerRect.left;
      const nextWidth = Math.max(SIDEBAR_MIN_W, Math.min(SIDEBAR_MAX_W, rawWidth));
      setSidebarWidth(nextWidth);
    };

    const handleMouseUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [setSidebarWidth]);

  React.useEffect(() => {
    if (!activeProfileId) {
      return;
    }

    if (suppressProfileSyncRef.current) {
      suppressProfileSyncRef.current = false;
      return;
    }

    const snapshot = currentSnapshot();
    setProfiles((prev) => {
      const exists = prev.some((p) => p.id === activeProfileId);
      if (!exists) {
        if (prev[0]?.id) {
          setActiveProfileId(prev[0].id);
        }
        return prev;
      }

      return prev.map((p) => (
        p.id === activeProfileId
          ? { ...p, name: axisConfig?.name || p.name, snapshot, updatedAt: Date.now() }
          : p
      ));
    });
  }, [nodes, lanes, laneOrder, nextId, axisConfig, activeProfileId]);

  const handleSelectProfile = useCallback((id) => {
    if (id === activeProfileId) {
      return;
    }
    const selected = profiles.find((p) => p.id === id);
    if (!selected) {
      return;
    }
    suppressProfileSyncRef.current = true;
    applySnapshot(selected.snapshot || {});
    setActiveProfileId(id);
  }, [activeProfileId, profiles, applySnapshot, setActiveProfileId]);

  const handleCreateProfile = useCallback(() => {
    const newProfile = makeProfile(getDefaultProfileName(profiles.length + 1), {
      nodes: INITIAL_NODES,
      lanes: INITIAL_LANES,
      laneOrder: INITIAL_LANE_ORDER,
      nextId: 19,
      axisConfig: INITIAL_AXIS,
    });
    suppressProfileSyncRef.current = true;
    setProfiles((prev) => [newProfile, ...prev]);
    setActiveProfileId(newProfile.id);
    applySnapshot(newProfile.snapshot);
  }, [profiles.length, getDefaultProfileName, setProfiles, setActiveProfileId, applySnapshot]);

  const handleDuplicateProfile = useCallback(() => {
    const base = profiles.find((p) => p.id === activeProfileId);
    if (!base) {
      return;
    }
    const cloned = makeProfile(`${base.name} (${t('tools.common.copySuffix')})`, base.snapshot);
    suppressProfileSyncRef.current = true;
    setProfiles((prev) => [cloned, ...prev]);
    setActiveProfileId(cloned.id);
    applySnapshot(cloned.snapshot);
  }, [profiles, activeProfileId, t, setProfiles, setActiveProfileId, applySnapshot]);

  const handleDeleteProfile = useCallback((id) => {
    if (profiles.length <= 1) {
      return;
    }

    const idx = profiles.findIndex((p) => p.id === id);
    if (idx < 0) {
      return;
    }

    const nextProfiles = profiles.filter((p) => p.id !== id);
    setProfiles(nextProfiles);

    if (id === activeProfileId) {
      const nextActive = nextProfiles[Math.min(idx, nextProfiles.length - 1)];
      if (nextActive) {
        suppressProfileSyncRef.current = true;
        applySnapshot(nextActive.snapshot || {});
        setActiveProfileId(nextActive.id);
      }
    }
  }, [profiles, activeProfileId, setProfiles, setActiveProfileId, applySnapshot]);

  const handleAddNode = useCallback((nodeData) => {
    const newNode = {
      id: nextId,
      status: 'todo',
      ...nodeData,
    };
    setNodes((prev) => [...prev, normalizeNode(newNode)]);
    setNextId((prev) => prev + 1);
    setShowAddBar(false);
    message.success(t('tools.timeline.nodeAdded'));
  }, [nextId, normalizeNode, setNodes, setNextId, t]);

  const handleUpdateNode = useCallback((updatedNode) => {
    setNodes((prev) => prev.map((n) => (n.id === updatedNode.id ? normalizeNode(updatedNode) : n)));
    setEditingNode(null);
    message.success(t('tools.timeline.nodeSaved'));
  }, [normalizeNode, setNodes, t]);

  const handleNodeDateChange = useCallback((nodeId, date) => {
    setNodes((prev) => prev.map((n) => (n.id === nodeId ? { ...n, date } : n)));
  }, [setNodes]);

  const handleQuickAddNode = useCallback(({ date, cat }) => {
    const newNode = {
      id: nextId,
      label: t('tools.timeline.defaultNodeName'),
      date,
      cat,
      priority: 'P1',
      status: 'todo',
    };

    setNodes((prev) => [...prev, normalizeNode(newNode)]);
    setNextId((prev) => prev + 1);
    setEditingNode(newNode);
    message.success(t('tools.timeline.nodeAdded'));
  }, [nextId, normalizeNode, setNodes, setNextId, t]);

  const handleDeleteNode = useCallback((nodeId) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setEditingNode(null);
    message.success(t('tools.timeline.nodeDeleted'));
  }, [setNodes, t]);

  const handleExportJSON = () => {
    const data = { nodes, lanes, laneOrder, nextId, axisConfig };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('tools.timeline.exported'));
  };

  const handleImportJSON = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (Array.isArray(data.nodes)) {
          setNodes(data.nodes.map(normalizeNode));
        }
        if (data.lanes && typeof data.lanes === 'object') {
          setLanes(data.lanes);
        }
        if (Array.isArray(data.laneOrder)) {
          setLaneOrder(data.laneOrder);
        }
        if (typeof data.nextId === 'number') {
          setNextId(data.nextId);
        }
        if (data.axisConfig && typeof data.axisConfig === 'object') {
          setAxisConfig({
            ...INITIAL_AXIS,
            ...data.axisConfig,
            months: Array.isArray(data.axisConfig.months) ? data.axisConfig.months : INITIAL_AXIS.months,
          });
        }
        message.success(t('tools.timeline.imported'));
      } catch {
        message.error(t('tools.timeline.importFailed'));
      }
    };
    reader.readAsText(file);
  };

  const handleExportSVG = () => {
    if (canvasRef.current) {
      canvasRef.current.exportSVG();
    }
  };

  const handleExportPNG = () => {
    if (canvasRef.current) {
      canvasRef.current.exportPNG();
    }
  };

  return (
    <div className={styles.timelinePage}>
      <main ref={mainContentRef} className={styles.mainContent}>
        <div
          className={styles.sidebarWrap}
          style={{ width: sidebarCollapsed ? 52 : sidebarWidth, minWidth: sidebarCollapsed ? 52 : SIDEBAR_MIN_W }}
        >
          <ToolHistorySidebar
            title={t('tools.common.processingHistory')}
            items={profiles}
            activeId={activeProfileId}
            onSelect={handleSelectProfile}
            onCreate={handleCreateProfile}
            onDuplicate={handleDuplicateProfile}
            onDelete={handleDeleteProfile}
            disableDelete={profiles.length <= 1}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          />
        </div>

        {!sidebarCollapsed && (
          <div
            className={styles.splitter}
            onMouseDown={() => {
              resizingRef.current = true;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
          />
        )}

        <div className={styles.workspace}>
          <Toolbar
            showAddBar={showAddBar}
            axisName={axisConfig?.name}
            axisGridDensity={axisConfig?.gridDensity || 'auto'}
            nodeStyle={axisConfig?.nodeStyle || 'tag'}
            onChangeAxisGridDensity={(gridDensity) => setAxisConfig((prev) => ({ ...prev, gridDensity }))}
            onChangeNodeStyle={(nodeStyle) => setAxisConfig((prev) => ({ ...prev, nodeStyle }))}
            onRenameAxis={(name) => {
              setAxisConfig((prev) => ({ ...prev, name }));
              setProfiles((prev) => prev.map((p) => (p.id === activeProfileId ? { ...p, name } : p)));
            }}
            onToggleAddBar={() => setShowAddBar(!showAddBar)}
            onAddNode={handleAddNode}
            onExportJSON={handleExportJSON}
            onImportJSON={handleImportJSON}
            onExportSVG={handleExportSVG}
            onExportPNG={handleExportPNG}
            onOpenLaneManager={() => setShowLaneManager(true)}
            onOpenAxisEditor={() => setShowAxisEditor(true)}
            lanes={lanes}
            laneOrder={laneOrder}
          />
          <Canvas
            ref={canvasRef}
            nodes={nodes.map(normalizeNode)}
            lanes={lanes}
            laneOrder={laneOrder}
            axisConfig={axisConfig}
            onNodeClick={(node) => setEditingNode(node)}
            onNodeDateChange={handleNodeDateChange}
            onCanvasBlankClick={handleQuickAddNode}
          />
        </div>
      </main>
      <PoweredBy />

      {editingNode && (
        <NodeEditor
          node={editingNode}
          lanes={lanes}
          laneOrder={laneOrder}
          onSave={handleUpdateNode}
          onDelete={handleDeleteNode}
          onClose={() => setEditingNode(null)}
        />
      )}

      {showLaneManager && (
        <LaneManager
          lanes={lanes}
          laneOrder={laneOrder}
          nodes={nodes.map(normalizeNode)}
          onUpdate={(newLanes, newOrder) => {
            setLanes(newLanes);
            setLaneOrder(newOrder);
          }}
          onNodesUpdate={(nextNodes) => setNodes(nextNodes.map(normalizeNode))}
          onClose={() => setShowLaneManager(false)}
        />
      )}

      {showAxisEditor && (
        <AxisEditor
          axisConfig={axisConfig}
          onSave={(nextAxis) => {
            setAxisConfig(nextAxis);
            setShowAxisEditor(false);
          }}
          onClose={() => setShowAxisEditor(false)}
        />
      )}
    </div>
  );
};

export default Timeline;
